<template>
  <main class="write-off-details">
    <div class="write-off-details__body" ref="body">
      <button-back />
      <div class="write-off-details__row">
        <h1 class="write-off-details__title">Списание товаров от {{ writeOff.createdDateTitle }}</h1>
      </div>
      <div class="write-off-details__options">
        <picker-items
          class="picker"
          v-bind:items="products"
          title="Выберите товар"
          v-model="shownProductPicker"
          v-on:add="selectingProduct"
          v-on:search="fetchOptions"
        >
          <template
            v-slot:default="{
              item,
              emitAdd,
              onSelectSetColor,
            }">
            <button
              class="picker__item__button"
              type="button"
              v-on:click="emitAdd(item.id); onSelectSetColor($event)">
              <span>{{ item.name }}</span>
            </button>
            <span>
              {{ item.unit }}
            </span>
          </template>
        </picker-items>
      </div>
      <div
        class="write-off-details__table__table-wrapper"
        :style="`min-height: ${getMinHeightTable}`"
      >
        <table
          class="write-off-details-table"
          v-if="hasFetchedWriteOffDetails"
        >
          <thead>
            <tr class="write-off-details-table__head-line">
              <th class="write-off-details-table__head-cell">№</th>
              <th class="write-off-details-table__head-cell">Название товара</th>
              <th class="write-off-details-table__head-cell">Единица измерения</th>
              <th class="write-off-details-table__head-cell">Количество на складе</th>
              <th class="write-off-details-table__head-cell">Количество</th>
              <th class="write-off-details-table__head-cell">Цена</th>
              <th class="write-off-details-table__head-cell">Итого</th>
              <th class="write-off-details-table__head-cell head-cell head-cell__button" />
            </tr>
          </thead>
          <tbody>
            <tr
              class="write-off-details-table__body-line"
              v-bind:key="index"
              v-for="{
                name,
                unit,
                count,
                index,
                price,
                getFinalPrice,
                countInWarehouse,
              } in writeOff.products"
            >
              <td class="write-off-details-table__body-cell">{{ index + 1 }}</td>
              <td class="write-off-details-table__body-cell">{{ name }}</td>
              <td class="write-off-details-table__body-cell">{{ unit }}</td>
              <td class="write-off-details-table__body-cell">{{ countInWarehouse }}</td>
              <td class="write-off-details-table__body-cell">
                <input-number
                  class="input-number"
                  v-bind:max="countInWarehouse"
                  min="1"
                  title=""
                  allowed-null
                  without-native-controls
                  v-bind:name="`products.${index}.count`"
                  v-bind:value="count"
                  v-bind:violations="productsInputErrors[index].count"
                  v-on:blur="onBlurCountInput(index, count)"
                  v-on:focus="onFocusCountInput(index, count)"
                  v-on:input="onChangeCount(index, $event)" />
              </td>
              <td class="write-off-details-table__body-cell">
                <input-layout class="writeoff-form__product-price" title="">
                  <price-input
                    v-bind:value="price"
                    v-on:input="onChangePrice(index, $event)"
                  />
                </input-layout>
              </td>
              <td class="write-off-details-table__body-cell">
                <input-layout title="">
                  <the-rub
                    currency="тнг"
                    v-bind:kop="getFinalPrice()"
                  />
                </input-layout>
              </td>
              <td class="write-off-details-table__body-cell body-cell body-cell__button">
                <button
                  class="write-off-details__buttons-delete"
                  type="button"
                  title="Удалить"
                  v-on:click="removeProduct(index)"
                />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="write-off-details__buttons">
        <button
          type="button"
          class="write-off-details__buttons-add"
          v-on:click="toggleShownPickerProduct"
        >
          Добавить товар
        </button>
        <button
          type="button"
          class="write-off-details__buttons-submit"
          v-bind:disabled="isAwaitRequest"
          v-on:click="editWriteOff"
        >
          Сохранить
        </button>
      </div>
    </div>
    <modal-dialog-prevent-leave
      v-bind:is-shown="isShownDialogLeaveConfirm"
      v-on:back="isShownDialogLeaveConfirm = false"
      v-on:leave="leaveWithoutSave"
      v-on:save="saveAndLeave" />
  </main>
</template>

<script>
import { builtInDialogMixin } from '@/components/ClinicPanelDialog.vue';
import notifierMixin from '@/mixins/notifierMixin';
import { Product, WriteOff, WriteOffRequest } from '@/storage/gateways/WriteOffGateway';
import InputLayout from '@/components/InputLayout.vue';
import PriceInput from '@/components/PriceInput.vue';
import InputNumber from '@/components/InputNumber.vue';
import TheRub from '@/components/TheRub.vue';
import { List } from 'monet';
import ButtonBack from '@/components/ButtonBack.vue';
import PickerItems from '@/components/PickerItems.vue';
import preventLeaveWithoutConfirmMixin from '@/mixins/preventLeaveWithoutConfirmMixin';
import ModalDialogPreventLeave from '@/components/ModalDialogPreventLeave.vue';

/**
 * @description Разрешить получение списка ошибок для поля ввода.
 * @param {string} cause Имя поля ввода.
 * @returns {function}
 */
const resolveInputErrors = (cause) => (vm) => (
  cause in vm.inputErrors ? vm.inputErrors[cause] : []
);

/**
 * @description Страница изменения списания товаров.
 * @type {Vue}
 */
export default {
  name: 'ClinicPanelWarehouseProductWriteOff',
  components: {
    ModalDialogPreventLeave,
    PickerItems,
    ButtonBack,
    TheRub,
    InputNumber,
    PriceInput,
    InputLayout,
  },
  computed: {
    /**
     * @description Предотвращать уход со страницы.
     * @type {boolean}
     */
    needPreventLeave(vm) {
      return vm.isWriteOffChange;
    },
    productInputErrors: resolveInputErrors('product'),
    productsInputErrors() {
      const countProducts = this.writeOff.products.length - 1;
      const errors = new Array(this.writeOff.products.length).fill(null);
      for (let i = 0; i <= countProducts; i += 1) {
        const count = this.inputErrors[`product-${i}-count`] ?? [];
        const price = this.inputErrors[`product-${i}-price`] ?? [];
        errors[i] = {
          count,
          price,
        };
      }
      return errors;
    },
    getMinHeightTable() {
      return this.writeOff?.products.length > 2 ? '275px' : 'min-content';
    },
    /**
     * @description Списание изменилось
     * @returns {boolean}
     */
    isWriteOffChange() {
      return this.writeOff.isChanged();
    },
  },
  created() {
    this.fetchOptions('', this.writeOffId);
    this.fetchWriteOff();
  },
  data() {
    return {
      isAwaitRequest: false,
      writeOff: new WriteOff(null, []),
      inputErrors: {},
      isAwaitDownloadRequest: false,
      products: [],
      hasFetchedWriteOffDetails: false,
      autocompleteKey: 0,
      selectedProduct: '',
      shownProductPicker: false,
    };
  },
  methods: {
    /**
     * @description Сохранить и покинуть
     * @returns {void}
     */
    saveAndLeave() {
      this.editWriteOff();
      this.actionAfterClosingDialogLeaveConfirm();
      this.isShownDialogLeaveConfirm = false;
    },
    /**
     * @description Переключить отображение пикера товаров.
     * @returns {void}
     */
    toggleShownPickerProduct() {
      this.shownProductPicker = !this.shownProductPicker;
    },
    /**
     * @description Очистить ошибки.
     */
    clearErrors() {
      this.inputErrors = {};
    },
    /**
     * @description Редактировать списания.
     */
    editWriteOff() {
      this.isAwaitRequest = true;
      const request = this.makeRequest();
      const onSuccess = ({ title }) => {
        this.notifySuccess(title);
        this.fetchWriteOff();
        this.fetchOptions('', this.writeOffId);
        this.inputErrors = {};
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
        this.inputErrors = error.allCauses();
      };
      this
        .$writeOffGateway
        .edit(request.getId(), request.toQuery())
        .then((result) => result.fold(onFail, onSuccess))
        .finally(() => { this.isAwaitRequest = false; });
    },
    /**
     * @description Получить списания.
     */
    fetchWriteOff() {
      const onSuccess = ({ writeOff }) => {
        this.writeOff = writeOff;
        this.hasFetchedWriteOffDetails = true;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this
        .$writeOffGateway
        .viewWriteOff(this.writeOffId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Получить опции.
     * @param {string}      search
     * @param {string|null} writeOffId
     * @returns {Promise}
     */
    fetchOptions(search = '', writeOffId = null) {
      const onSuccess = ({ products }) => {
        this.products = products;
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      return this
        .$writeOffGateway
        .viewOptions(search, writeOffId)
        .then((result) => result.fold(onFail, onSuccess));
    },
    /**
     * @description Обработать выбор товара.
     * @returns {void}
     */
    selectingProduct(productId) {
      const product = this.products.find((item) => item.id === productId);
      this.selectedProduct = '';
      this.autocompleteKey += 1;
      this.onAddingPriceProduct(product);
      this.clearErrors();
    },
    /**
     * @description Обработать добавление товара.
     * @param {Object} product Товар.
     * @returns {void}
     */
    onAddingPriceProduct(product) {
      const addingProduct = new Product(
        this.writeOff.products.length,
        product.id,
        product.name,
        0,
        product.unit,
        product.price,
        product.countInWarehouse,
      );
      this.$set(this.writeOff.products, this.writeOff.products.length, addingProduct);
    },
    /**
     * @description Обработать изменение количества товара.
     * @param {number} productIndex Индекс товара.
     * @param {number} count        Количество.
     * @returns {void}
     */
    onChangeCount(productIndex, count) {
      const updater = (product) => product.setCount(count);
      this.writeOff.products = this.updateProductByIndex(updater)(productIndex);
    },
    /**
     * @description Обработать изменения единицы измерения.
     * @param {number} productIndex Индекс товара.
     * @param {string} price        Цена товара.
     * @returns {void}
     */
    onChangePrice(productIndex, price) {
      const updater = (product) => product.setPrice(price);
      this.writeOff.products = this.updateProductByIndex(updater)(productIndex);
    },
    /**
     * @description Обработать потерю фокуса с поля количества товара.
     * @param {number} serviceIndex Индекс товара.
     * @param {number} count        Количество.
     * @returns {void}
     */
    onBlurCountInput(serviceIndex, count) {
      if (count === '') {
        this.onChangeCount(serviceIndex, '1');
      }
    },
    /**
     * @description Обработать фокус на поле количества товара.
     * @param {number} serviceIndex Индекс товара.
     * @param {number} count        Количество.
     * @returns {void}
     */
    onFocusCountInput(serviceIndex, count) {
      if (count.toString().charAt(0) === '0') {
        this.onChangeCount(serviceIndex, 0);
      }
    },
    /**
     * @description Создать запрос.
     * @returns {WriteOffRequest}
     */
    makeRequest() {
      return new WriteOffRequest(
        this.writeOffId,
        this.writeOff.products,
      );
    },
    /**
     * @description Удалить товар поступления.
     * @param {number} productIndex Индекс товара.
     * @returns {void}
     */
    removeProduct(productIndex) {
      const exclude = (index) => (product) => product.index !== index;
      const reindex = (acc, product) => acc.cons(
        new Product(
          acc.size(),
          product.id,
          product.name,
          product.count,
          product.unit,
          product.price,
          product.countInWarehouse,
        ),
      );
      this.writeOff.products = List
        .fromArray(this.writeOff.products)
        .filter(exclude(productIndex))
        .foldLeft(List())(reindex)
        .reverse()
        .toArray();
    },
    /**
     * @description Получить обновление товара списания по индексу.
     * @param {function(Product): Product} updater Обновление товара.
     * @returns {function(Number): Product[]}
     */
    updateProductByIndex(updater) {
      return (index) => this.writeOff.products.map(
        (product) => (product.index === index ? updater(product) : product),
      );
    },
  },
  mixins: [
    notifierMixin,
    builtInDialogMixin,
    preventLeaveWithoutConfirmMixin,
  ],
  props: {
    writeOffId: {
      required: true,
      type: String,
    },
  },
};
</script>

<style lang="scss">
@use "@/scss/buttons";

.write-off-details {
  table {
    th:first-child {
      width: 60px;
    }
    th:nth-child(n+2) {
      width: 25%;
    }
    th.head-cell {
      &__button {
        width: 50px;
      }
    }
    .body-cell {
      &__button {
        text-align: center;
      }
    }
  }
}

.write-off {
  &-details {
    display: flex;
    flex-direction: row;
    width: 100%;
    height: 100%;
    &__filters {
      font-size: 14px;
      display: flex;
      align-items: center;
      gap: 20px;
      &-label {
        color: gray;
        font-size: 12px;
      }
      &-additional {
        display: flex;
        gap:10px;
        margin-top: 20px;
      }
    }
    &__categories {
      width: 280px;
      &__caption {
        font-size: 14px;
        line-height: 17px;
        font-weight: 700;
        border-bottom: 2px solid #0489e0;
        padding: 10px;
      }
      &__container {
        display: flex;
        flex-direction: column;
        gap: 15px;
        width: 280px;
        background: #f2f7fa;
        padding: 20px 10px;
      }
    }
    &__buttons {
      justify-self: end;
      display: grid;
      grid-template-columns: repeat(2, minmax(150px, 240px));
      grid-gap: 20px;
      max-width: 760px;
      margin: 20px 0 0 0;
      &-filter {
        margin-top: 25px;
        display: flex;
        align-items: center;
        gap: 5px;
        max-width: 175px;
        width: 100%;
        color: #1F85C8;
        font-size: 16px;
        font-weight: 400;
        line-height: 20px;
        >img {
          width: 15px;
          height: 15px;
        }
      }
      &-add {
        @include buttons.btn-transparent(100%, 48px, 16px);
        &::before {
          content: "+";
          margin-right: 4px;
        }
      }

      &-export {
        @include buttons.btn-transparent(100%, 48px, 16px);

        &:before {
          content: "";
          background-image: url("@/assets/img/download-file-blue.svg");
          background-repeat: no-repeat;
          background-size: contain;
          width: 20px;
          height: 20px;
          margin: 0 8px 0 0;
        }

        &:disabled {
          color: var(--unfocus-color);
          border-color: var(--unfocus-color);
          background-color: transparent;
        }
      }

      &-delete {
        display: inline-block;
        width: 25px;
        height: 25px;
        background: no-repeat 50%;
        background-image: url("@/assets/img/exit-grey.svg");
        background-size: 12px 12px;
        cursor: pointer;
        &:hover {
          background-image: url("@/assets/img/exit-blue.svg");
        }
      }

      &-submit {
        @include buttons.btn-blue(100%, 48px, 16px);
        flex-shrink: 0;
        max-width: 250px;
      }
    }

    &__body {
      display: flex;
      flex-direction: column;
      gap: 2rem;
      padding: 38px 20px 48px 23px;
      width: 100%;
      overflow-y: auto;
      overflow-x: hidden;
    }

    &__row {
      display: flex;
      flex-direction: row;
      justify-content: space-between;
    }

    &__options {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(250px, 450px));
      gap: 25px;
      max-width: 850px;
      &__input {
        width: 100%;
      }
    }

    &__search {
      max-width: 400px;
    }

    &__table {
      display: flex;
      flex-direction: column;
      gap: 2rem;
      overflow: scroll;
      padding: 2rem 1rem;
      height: 100%;

      &__table-wrapper {
        min-height: 200px;
        max-height: 650px;
        overflow-x: auto;
      }
    }

    &-table {
      position: relative;
      border-collapse: collapse;
      font-size: .875rem;
      line-height: 1rem;
      overflow: auto;
      table-layout: fixed;
      width: 100%;

      &__head-line {
        border-bottom: 2px solid var(--action-color);
        font-weight: 700;
      }

      &__head-cell {
        position: sticky;
        top: 0;
        padding: 10px 8px;
        text-align: left;
        background-color: #ffffff;
        z-index: 1;
        &::after {
          content: '';
          position: absolute;
          left: 0;
          bottom: -2px;
          width: 100%;
          border-bottom: 2px solid var(--action-color);
        }
      }

      &__body-line {
        border-bottom: 1px solid var(--fill-color);
      }

      &__body-cell {
        overflow: hidden;
        padding: 15px 8px;
        text-align: left;
        text-overflow: ellipsis;
        text-transform: none;
        white-space: pre-line;
        .input-number {
          max-width: 110px;
        }
      }
    }
  }
}

@media screen and (max-width: 960px) {
  .write-off {
    &-details {
      flex-direction: column;

      &__row {
        flex-direction: column;
        gap: 15px;
      }

      &__search {
        max-width: 100%;
      }
    }
  }
}

@media screen and (max-width: 950px) {
  .write-off-details {
    .write-off-details-table  {
      min-width: 950px;
    }
  }
}

@media screen and (max-width: 640px) {
  .write-off {
    &-details {
      &__body {
        padding: 38px 10px 140px;
        width: 100%;
        overflow-y: scroll;
      }
    }
  }
}

@media screen and (max-height: 760px) {
  .write-off {
    &-details {
      &__body {
        padding-bottom: 38px;
      }
    }
  }
}

.picker {
  &__item {
    display: grid;
    grid-template-columns: 1fr auto;
    grid-gap: 5px;
    font-weight: 400;
    font-size: 16px;
    line-height: 19px;
    &__button {
      max-width: 400px;
      text-align: left;
      transition: color 0.2s;
      > span.selected {
        color: #0569ac;
      }
      @media (hover: hover) {
        &:hover {
          color: #0076C4;
        }
      }
      &:active {
        color: #0569ac;
      }
    }
    &__bottom {
      text-align: center;
      transition: color 0.2s;
      @include buttons.btn-transparent(100%, 48px, 16px);
    }
  }
}
</style>
